import { useLayoutEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./WhyChooseGSAP.css";

gsap.registerPlugin(ScrollTrigger);

const FEATURES = [
  {
    title: "Expert Training",
    text: "DGCA-aligned curriculum designed by aviation professionals with over 5+ years of industry experience.",
    icon: "🎯"
  },
  {
    title: "Safety First",
    text: "Industry-grade safety protocols and advanced risk assessment for secure UAV operations.",
    icon: "🛡️"
  },
  {
    title: "Future Ready",
    text: "Advanced drone training focused on emerging technologies and real-world UAV applications.",
    icon: "🚀"
  }
];

export default function WhyChooseGSAP() {
  const sectionRef = useRef(null);
  const cardsRef = useRef([]);
  const contentRef = useRef(null);
  const titleRef = useRef(null);
  const paragraphRef = useRef(null);
  const navigate = useNavigate();

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const cards = cardsRef.current;
      const title = titleRef.current;
      const paragraph = paragraphRef.current;

      const isMobile = window.innerWidth <= 768;
      const isTablet = window.innerWidth <= 992;

      // On mobile, skip pinning entirely — just use fade-in on scroll
      if (isMobile) {
        gsap.set([title, paragraph], { y: 40, opacity: 0 });
        gsap.set(cards, { y: 40, opacity: 0 });

        gsap.to([title, paragraph], {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.15,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 80%",
            toggleActions: "play none none none"
          }
        });

        cards.forEach((card, i) => {
          gsap.to(card, {
            y: 0,
            opacity: 1,
            duration: 0.7,
            ease: "power3.out",
            scrollTrigger: {
              trigger: card,
              start: "top 85%",
              toggleActions: "play none none none"
            },
            delay: i * 0.1
          });
        });

        return;
      }

      // Tablet & desktop: use pin with a shorter duration on tablet
      const pinMultiplier = isTablet ? 1.2 : 1.6;

      gsap.set([title, paragraph], { y: 60, opacity: 0 });
      gsap.set(cards, { x: 120, opacity: 0, rotationY: 15 });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: `+=${window.innerHeight * pinMultiplier}`,
          pin: true,
          scrub: 1,
          anticipatePin: 1,
          markers: false
        }
      });

      tl.to(title, { y: 0, opacity: 1, duration: 1.2, ease: "power3.out" }, 0.1)
        .to(paragraph, { y: 0, opacity: 1, duration: 1.2, ease: "power3.out" }, 0.25);

      cards.forEach((card, i) => {
        tl.to(
          card,
          { x: 0, opacity: 1, rotationY: 0, duration: 1, ease: "back.out(1.2)" },
          0.5 + i * 0.35
        );
      });

      // Hover lift
      cards.forEach((card) => {
        card.addEventListener("mouseenter", () => {
          gsap.to(card, { y: -12, duration: 0.35, ease: "power2.out" });
        });
        card.addEventListener("mouseleave", () => {
          gsap.to(card, { y: 0, duration: 0.35, ease: "power2.out" });
        });
      });

      // Gentle float — only on desktop
      if (!isTablet) {
        cards.forEach((card, i) => {
          gsap.to(card, {
            y: -8,
            duration: 2 + i * 0.3,
            repeat: -1,
            yoyo: true,
            ease: "sine.inOut",
            delay: i * 0.2
          });
        });
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="wcgs-section">
      <div className="wcgs-container">
        <div className="wcgs-inner">
          <div ref={contentRef} className="wcgs-content">
            <div className="wcgs-text-container">
              <div className="wcgs-title-wrapper">
                <span className="wcgs-subtitle">WHY CHOOSE US</span>
                <h2 ref={titleRef} className="wcgs-title">
                  Elevate Your <span className="wcgs-highlight">Drone Career</span> with Industry-Leading Training
                </h2>
              </div>

              <div className="wcgs-paragraph-wrapper">
                <p ref={paragraphRef} className="wcgs-paragraph">
                  At Flytutor, we combine cutting-edge technology with expert instruction to deliver
                  the most comprehensive drone training available. Our programs are designed not just
                  to teach you how to fly, but to prepare you for a successful career in the rapidly
                  expanding UAV industry. With our innovative approach and commitment to excellence,
                  we ensure every student graduates with confidence, competence, and competitive advantage.
                </p>

                <div className="wcgs-stats">
                  <div className="wcgs-stat">
                    <span className="wcgs-stat-number">100%</span>
                    <span className="wcgs-stat-label">Success Rate</span>
                  </div>
                  <div className="wcgs-stat">
                    <span className="wcgs-stat-number">300+</span>
                    <span className="wcgs-stat-label">Drone Pilots</span>
                  </div>
                  <div className="wcgs-stat">
                    <span className="wcgs-stat-number">24/7</span>
                    <span className="wcgs-stat-label">Support</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="wcgs-cards-container">
              <div className="wcgs-cards">
                {FEATURES.map((f, i) => (
                  <div
                    key={i}
                    className="wcgs-card"
                    ref={(el) => (cardsRef.current[i] = el)}
                  >
                    <div className="wcgs-card-icon">{f.icon}</div>
                    <div className="wcgs-card-content">
                      <h3 className="wcgs-card-title">{f.title}</h3>
                      <p className="wcgs-card-text">{f.text}</p>
                    </div>
                    <div className="wcgs-card-decoration">
                      <div className="wcgs-card-line"></div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="wcgs-cta">
                <p className="wcgs-cta-text">Ready to take flight with your career?</p>
                <button className="wcgs-cta-button" onClick={() => navigate("/course")}>
                  Enroll Now
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}